'use client'

import React, { useEffect, useState } from 'react'
import { Star, Send, CheckCircle } from 'lucide-react'
import { apiAddComment } from '@/apis/offers'
import { toast } from 'react-toastify'

interface Props { offer_id: string; company_id: string }

const CommentSection = ({ offer_id, company_id }: Props) => {
    const [rating, setRating] = useState(0)
    const [hover, setHover] = useState(0)
    const [comment, setComment] = useState('')
    const [loading, setLoading] = useState(false)
    const [hasRated, setHasRated] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    useEffect(() => {
        const lastRated = localStorage.getItem(`hasRated_${offer_id}`)
        if (lastRated && new Date().getTime() - parseInt(lastRated) < 86400000) setHasRated(true)
    }, [offer_id])

    const labels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (hasRated) { toast.info('You have already rated this product today.'); return }
        if (!rating && !comment.trim()) { toast.error('Please select a rating or add a comment.'); return }
        setLoading(true)
        try {
            const res = await apiAddComment(offer_id, company_id, comment, rating.toString())
            if (res.status === 'success') {
                localStorage.setItem(`hasRated_${offer_id}`, new Date().getTime().toString())
                setHasRated(true); setSubmitted(true); setRating(0); setComment('')
            } else { toast.error('Something went wrong. Please try again.') }
        } catch { toast.error('An error occurred while submitting.') }
        finally { setLoading(false) }
    }

    if (submitted || hasRated) return (
        <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(139,88,38,0.1)' }}>
                <CheckCircle size={24} style={{ color: '#8C5826' }} />
            </div>
            <p className="text-sm font-black" style={{ color: '#1a1a1a' }}>Thanks for your feedback!</p>
            <p className="text-xs" style={{ color: '#9ca3af' }}>Your review has been submitted.</p>
        </div>
    )

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {/* Stars */}
            <div>
                <div className="flex items-center gap-1.5 mb-1" onMouseLeave={() => setHover(0)}>
                    {[1, 2, 3, 4, 5].map(star => (
                        <button key={star} type="button"
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHover(star)}
                            className="transition-transform hover:scale-110">
                            <Star size={28} className={`transition-colors duration-150 ${star <= (hover || rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`} />
                        </button>
                    ))}
                </div>
                {(hover || rating) > 0 && (
                    <span className="text-xs font-bold" style={{ color: '#8C5826' }}>{labels[hover || rating]}</span>
                )}
            </div>

            {/* Comment */}
            <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: '#6b7280' }}>
                    Your Comment <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span>
                </label>
                <textarea value={comment} onChange={e => setComment(e.target.value)}
                    placeholder="What did you think of this product?"
                    rows={3}
                    className="w-full px-3 py-2.5 text-sm rounded-xl focus:outline-none focus:ring-1 resize-none"
                    style={{ border: '1px solid #f0ede8', background: '#fffbf0', color: '#1a1a1a' }} />
            </div>

            <button type="submit" disabled={loading || !rating}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-black rounded-xl transition-all disabled:opacity-50"
                style={{ background: '#8C5826', color: '#ffffff' }}>
                {loading
                    ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <><Send size={14} /> Submit Review</>
                }
            </button>
        </form>
    )
}

export default CommentSection
